import { forwardRef, useImperativeHandle, useCallback, useEffect, useRef } from "react";
import { motion, useAnimate } from "framer-motion";

export type IconEasing =
  | "linear"
  | "easeIn"
  | "easeOut"
  | "easeInOut"
  | "circIn"
  | "circOut"
  | "circInOut"
  | "backIn"
  | "backOut"
  | "backInOut"
  | "anticipate";

export interface AnimatedIconProps {
  size?: number | string;
  color?: string;
  strokeWidth?: number | string;
  className?: string;
  loop?: boolean;
}

export interface AnimatedIconHandle {
  startAnimation: () => void;
  stopAnimation: () => void;
}

const useLoopingAnimation = (loop: boolean, start: () => void, stop: () => void) => {
  useEffect(() => {
    if (loop) {
      start();
    } else {
      stop();
    }
  }, [loop, start, stop]);
};

// --- PREVIOUS ICONS ---

export const PlugConnectedIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      const opt = { duration: 0.8, repeat: loop ? Infinity : 0, repeatType: "reverse" as const, ease: "easeInOut" };
      animate(".plug-upper-part", { y: 2, x: -2 }, opt as any);
      animate(".plug-lower-leg", { opacity: [1, 0, 1] }, opt as any);
      animate(".plug-lower-part", { y: -2, x: 2 }, opt as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".plug-upper-part, .plug-lower-leg, .plug-lower-part", { y: 0, x: 0, opacity: 1 }, { duration: 0.3 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <path d="M7 12l5 5l-1.5 1.5a3.536 3.536 0 1 1 -5 -5l1.5 -1.5z" className="plug-lower-part" />
        <path d="M17 12l-5 -5l1.5 -1.5a3.536 3.536 0 1 1 5 5l-1.5 1.5z" className="plug-upper-part" />
        <path d="M3 21l2.5 -2.5" className="plug-lower-part" />
        <path d="M18.5 5.5l2.5 -2.5" className="plug-upper-part" />
        <path d="M10 11l-2 2" className="plug-lower-part plug-lower-leg" />
        <path d="M13 14l-2 2" className="plug-lower-part plug-lower-leg" />
      </motion.svg>
    );
  }
);

export const DrumIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      const opt = { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" };
      animate(".sticks", { y: [0, 3, 0] }, opt as any);
      animate(".drum-body", { scaleY: [1, 0.95, 1] }, opt as any);
      animate(".drum-top", { scaleX: [1, 1.05, 1] }, opt as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".sticks, .drum-body, .drum-top", { x: 0, y: 0, scaleX: 1, scaleY: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <path className="sticks" d="m2 2 8 8" />
        <path className="sticks" d="m22 2-8 8" />
        <motion.ellipse className="drum-top" cx="12" cy="9" rx="10" ry="5" style={{ transformOrigin: "12px 9px" }} />
        <path className="drum-body" d="M7 13.4v7.9" />
        <path className="drum-body" d="M12 14v8" />
        <path className="drum-body" d="M17 13.4v7.9" />
        <motion.path className="drum-body" style={{ transformOrigin: "12px 13px" }} d="M2 9v8a10 5 0 0 0 20 0V9" />
      </motion.svg>
    );
  }
);

export const SoupIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".steam", { y: [0, -4], opacity: [1, 0] }, { duration: 0.8, repeat: loop ? Infinity : 2, ease: "easeOut" });
      animate(".bowl", { rotate: [0, 1, -1, 0] }, { duration: 0.4, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => {
      animate(".steam", { y: 0, opacity: 1 }, { duration: 0.3 });
      animate(".bowl", { rotate: 0 }, { duration: 0.3 });
    }, [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop} style={{ overflow: "visible" }}>
        <motion.g className="bowl" style={{ transformOrigin: "12px 16px" }}>
          <path d="M12 21a9 9 0 0 0 9-9H3a9 9 0 0 0 9 9Z" />
          <path d="M7 21h10" />
          <path d="M19.5 12 22 6" />
        </motion.g>
        <motion.g className="steam">
          <path d="M16.25 3c.27.1.8.53.75 1.36-.06.83-.93 1.2-1 2.02-.05.78.34 1.24.73 1.62" />
          <path d="M11.25 3c.27.1.8.53.74 1.36-.05.83-.93 1.2-.98 2.02-.06.78.33 1.24.72 1.62" />
          <path d="M6.25 3c.27.1.8.53.75 1.36-.06.83-.93 1.2-1 2.02-.05.78.34 1.24.74 1.62" />
        </motion.g>
      </motion.svg>
    );
  }
);

export const CoffeeIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".steam", { y: [0, -3], opacity: [1, 0] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".steam", { y: 0, opacity: 1 }, { duration: 0.5 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop} style={{ overflow: "visible" }}>
        <path className="steam" d="M6 2v3" />
        <path className="steam" d="M10 2v3" />
        <path className="steam" d="M14 2v3" />
        <path d="M16 8a1 1 0 0 1 1 1v8a4 4 0 0 1-4 4H7a4 4 0 0 1-4-4V9a1 1 0 0 1 1-1h14a4 4 0 1 1 0 8h-1" />
      </motion.svg>
    );
  }
);

export const StarIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".star-fill", { opacity: [0, 1], scale: [0.8, 1] }, { duration: 0.4, repeat: loop ? Infinity : 0, repeatType: "reverse", ease: "easeOut" });
      animate(".star-outline", { scale: [1, 1.1, 1], rotate: [0, -5, 5, 0] }, { duration: 0.5, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => {
      animate(".star-fill", { opacity: 0 }, { duration: 0.3 });
      animate(".star-outline", { scale: 1, rotate: 0 }, { duration: 0.3 });
    }, [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.path className="star-fill" d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" fill={color} opacity={0} style={{ transformOrigin: "center" }} />
        <motion.path className="star-outline" d="M12 17.75l-6.172 3.245l1.179 -6.873l-5 -4.867l6.9 -1l3.086 -6.253l3.086 6.253l6.9 1l-5 4.867l1.179 6.873z" style={{ transformOrigin: "center" }} />
      </motion.svg>
    );
  }
);

export const TruckElectricIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(async () => {
      const opt = { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" };
      await animate(".truck", { x: [0, 5, 0] }, opt as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".truck", { x: 0, opacity: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.g className="truck">
          <path d="M14 19V7a2 2 0 0 0-2-2H9" />
          <path d="M15 19H9" />
          <path d="M19 19h2a1 1 0 0 0 1-1v-3.65a1 1 0 0 0-.22-.62L18.3 9.38a1 1 0 0 0-.78-.38H14" />
          <path d="M2 13v5a1 1 0 0 0 1 1h2" />
          <path d="M4 3 2.15 5.15a.495.495 0 0 0 .35.86h2.15a.47.47 0 0 1 .35.86L3 9.02" />
          <circle cx="17" cy="19" r="2" />
          <circle cx="7" cy="19" r="2" />
        </motion.g>
      </motion.svg>
    );
  }
);

export const PassportIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".flap", { x: [0, 2, 0] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" });
      animate(".globe", { pathLength: [0, 1] }, { duration: 0.8, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => {
      animate(".flap", { x: 0 }, { duration: 0.3 });
      animate(".globe", { pathLength: 1 }, { duration: 0.3 });
    }, [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <path d="M2 9c0-3.3 0-4.95 1.025-5.975S5.7 2 9 2h3c3.3 0 4.95 0 5.975 1.025S19 5.7 19 9v6c0 3.3 0 4.95-1.025 5.975S15.3 22 12 22H9c-3.3 0-4.95 0-5.975-1.025S2 18.3 2 15z" />
        <path d="M7 17h8" />
        <motion.path className="flap" d="M12.95 22c2.645 0 3.967 0 4.917-.756.95-.757 1.252-2.051 1.858-4.64l1.917-8.197c.335-1.433.503-2.15.2-2.67C21.287 4.796 19.878 5 18.958 5" />
        <motion.path className="globe" d="M11 14a4 4 0 0 1 0-8M11 14a4 4 0 0 0 0-8" />
        <motion.path className="globe" d="M11 14s1.5-1.79 1.5-4S11 6 11 6M11 14s-1.5-1.79-1.5-4S11 6 11 6" />
      </motion.svg>
    );
  }
);

export const PlayerIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".play-icon", { scale: [1, 0.8, 1] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".play-icon", { scale: 1 }, { duration: 0.4 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="currentColor" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <path className="play-icon" style={{ transformOrigin: "center" }} d="M6 4v16a1 1 0 0 0 1.524 .852l13 -8a1 1 0 0 0 0 -1.704l-13 -8a1 1 0 0 0 -1.524 .852z" />
      </motion.svg>
    );
  }
);

// --- NEW FESTIVAL ICONS ---

export const HeartIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".heart", { scale: [1, 1.15, 1, 1.25, 1] }, { duration: 0.8, repeat: loop ? Infinity : 0, ease: "easeOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".heart", { scale: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop} style={{ overflow: "visible" }}>
        <motion.path className="heart" style={{ transformOrigin: "50% 50%" }} d="M19.5 12.572l-7.5 7.428l-7.5 -7.428a5 5 0 1 1 7.5 -6.566a5 5 0 1 1 7.5 6.572" />
      </motion.svg>
    );
  }
);

export const CameraIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".lens", { scale: [1, 1.2, 1] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeOut" });
      animate(".body", { scale: [1, 0.95, 1] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".lens, .body", { scale: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.path className="body" style={{ transformOrigin: "center" }} d="M13.997 4a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v9a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 1.759-1.048l.489-.904A2 2 0 0 1 10.004 4z" />
        <motion.circle className="lens" style={{ transformOrigin: "12px 13px" }} cx="12" cy="13" r="3" />
      </motion.svg>
    );
  }
);

export const WifiIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      const opt = { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" };
      animate(".wave-1", { opacity: [1, 0.3, 1] }, opt as any);
      animate(".wave-2", { opacity: [1, 0.3, 1] }, { ...opt, delay: 0.15 } as any);
      animate(".wave-3", { opacity: [1, 0.3, 1] }, { ...opt, delay: 0.3 } as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".wave-1, .wave-2, .wave-3", { opacity: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <path d="M12 18l.01 0" />
        <motion.path className="wave-1" d="M9.172 15.172a4 4 0 0 1 5.656 0" />
        <motion.path className="wave-2" d="M6.343 12.343a8 8 0 0 1 11.314 0" />
        <motion.path className="wave-3" d="M3.515 9.515c4.686 -4.687 12.284 -4.687 17 0" />
      </motion.svg>
    );
  }
);

export const BatteryIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2.5, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".battery-bolt", { pathLength: [0, 1], opacity: [0, 1, 0.4, 1] }, { duration: 1.2, repeat: loop ? Infinity : 0, ease: "easeInOut" } as any);
      animate(".battery-body", { scale: [1, 1.04, 1] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" } as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".battery-bolt", { pathLength: 1, opacity: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 48 48" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="square" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <path d="M46 20V28" />
        <motion.path className="battery-body" style={{ transformOrigin: "center" }} d="M37 10H8C5.23858 10 3 12.2386 3 15V33C3 35.7614 5.23858 38 8 38H37C39.7614 38 42 35.7614 42 33V15C42 12.2386 39.7614 10 37 10Z" />
        <motion.path className="battery-bolt" style={{ transformOrigin: "center" }} d="M33.5 25.6667L22.5 19V29L11.5 22.3333" />
      </motion.svg>
    );
  }
);

export const FlameIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".flame-path", { scale: [1, 1.1, 0.9, 1.05, 1], y: [0, -2, 0] }, { duration: 0.8, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".flame-path", { scale: 1, y: 0 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.path className="flame-path" style={{ transformOrigin: "center 80%" }} d="M12 12c2.333 -4.667 3.5 -7 3.5 -10c0 2.5 -1 5 -2.5 8c-1.5 -3 -1.5 -5 -1.5 -8c0 3 -1.167 5.333 -3.5 10c0 2.5 1.5 5 3.5 7c2 -2 2.5 -4.5 2.5 -7c0 2.5 .5 5 2.5 7c2 -2 3.5 -4.5 3.5 -7c0 3 -1.167 5.333 -3.5 10" />
      </motion.svg>
    );
  }
);

export const TrashIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".trash-lid", { rotate: loop ? [0, -25, 0] : -25, y: loop ? [0, -4, 0] : -4 }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeOut" } as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".trash-lid", { rotate: 0, y: 0 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.path className="trash-lid" style={{ transformOrigin: "50% 100%" }} d="M4 7l16 0 M9 7v-3a1 1 0 0 1 1 -1h4a1 1 0 0 1 1 1v3" />
        <path d="M10 11l0 6 M14 11l0 6 M5 7l1 12a2 2 0 0 0 2 2h8a2 2 0 0 0 2 -2l1 -12" />
      </motion.svg>
    );
  }
);

export const UsersIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      const opt = { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" };
      animate(".user-primary", { y: [0, -2, 0], scale: [1, 1.05, 1] }, opt as any);
      animate(".user-secondary", { x: [0, 1, 0], opacity: [1, 0.8, 1] }, opt as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".user-primary, .user-secondary", { y: 0, x: 0, scale: 1, opacity: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.g className="user-primary"><path d="M9 7m-4 0a4 4 0 1 0 8 0a4 4 0 1 0 -8 0" /><path d="M3 21v-2a4 4 0 0 1 4 -4h4a4 4 0 0 1 4 4v2" /></motion.g>
        <motion.g className="user-secondary"><path d="M16 3.13a4 4 0 0 1 0 7.75" /><path d="M21 21v-2a4 4 0 0 0 -3 -3.85" /></motion.g>
      </motion.svg>
    );
  }
);

export const CartIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      const opt = { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" };
      animate(".cart-body", { x: [0, 3, 0] }, opt as any);
      animate(".cart-wheel", { rotate: [0, 360] }, opt as any);
    }, [animate, loop]);
    const stop = useCallback(() => animate(".cart-body, .cart-wheel", { x: 0, rotate: 0 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.g className="cart-body"><path d="M17 17h-11v-14h-2" /><path d="M6 5l14 1l-1 7h-13" /></motion.g>
        <motion.circle className="cart-wheel" style={{ transformOrigin: "6px 19px" }} cx="6" cy="19" r="2" />
        <motion.circle className="cart-wheel" style={{ transformOrigin: "17px 19px" }} cx="17" cy="19" r="2" />
      </motion.svg>
    );
  }
);

export const BellIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".bell", { rotate: [0, -10, 10, -10, 10, 0] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".bell", { rotate: 0 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.path className="bell" style={{ transformOrigin: "50% 0%" }} d="M10 5a2 2 0 0 1 4 0a7 7 0 0 1 4 6v3a4 4 0 0 0 2 3h-16a4 4 0 0 0 2 -3v-3a7 7 0 0 1 4 -6" />
        <path d="M9 17v1a3 3 0 0 0 6 0v-1" />
      </motion.svg>
    );
  }
);

export const MagnifierIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".glass", { x: [0, 2, -2, 0], y: [0, -2, 2, 0] }, { duration: 1, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".glass", { x: 0, y: 0 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.g className="glass"><circle cx="10" cy="10" r="7" /><path d="M21 21l-6 -6" /></motion.g>
      </motion.svg>
    );
  }
);

export const MessageCircleIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const start = useCallback(() => {
      animate(".bubble", { scale: [1, 1.1, 1], opacity: [1, 0.8, 1] }, { duration: 0.6, repeat: loop ? Infinity : 0, ease: "easeInOut" });
    }, [animate, loop]);
    const stop = useCallback(() => animate(".bubble", { scale: 1, opacity: 1 }, { duration: 0.2 }), [animate]);
    useImperativeHandle(ref, () => ({ startAnimation: start, stopAnimation: stop }));
    useLoopingAnimation(loop, start, stop);
    return (
      <motion.svg ref={scope} width={size} height={size} viewBox="0 0 24 24" fill="none" stroke={color} strokeWidth={strokeWidth} strokeLinecap="round" strokeLinejoin="round" className={className} onHoverStart={loop ? undefined : start} onHoverEnd={loop ? undefined : stop}>
        <motion.path className="bubble" style={{ transformOrigin: "center" }} d="M3 20l1.3 -3.9a9 8 0 1 1 3.4 2.9l-4.7 1z" />
      </motion.svg>
    );
  }
);

// --- AUTOMATICALLY INTEGRATED HOVER ICONS ---

export const WalletIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, className = "", strokeWidth = 2, color = "currentColor", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = async () => {
      // Flap opens outward
      animate(
        ".flap",
        { rotateY: 25, x: 2 },
        { duration: 0.4, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );

      // Wallet expands slightly
      animate(
        ".wallet-body",
        { scale: 1.05 },
        { duration: 0.4, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    };

    const stop = async () => {
      animate(".flap", { rotateY: 0, x: 0 }, { duration: 0.4, ease: "easeIn" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const });

      animate(".wallet-body", { scale: 1 }, { duration: 0.4, ease: "easeIn" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        color={color}
        stroke="currentColor"
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        style={{ perspective: "400px" }}
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />

        <motion.path
          className="wallet-body"
          d="M17 8v-3a1 1 0 0 0 -1 -1h-10a2 2 0 0 0 0 4h12a1 1 0 0 1 1 1v3m0 4v3a1 1 0 0 1 -1 1h-12a2 2 0 0 1 -2 -2v-12"
        />

        <motion.path
          className="flap"
          d="M20 12v4h-4a2 2 0 0 1 0 -4h4"
          style={{
            originX: "50%",
            originY: "50%",
            transformStyle: "preserve-3d",
          }}
        />
      </motion.svg>
    );
  },
);

WalletIcon.displayName = "WalletIcon";

export const CreditCardIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = async () => {
      animate(
        ".card-body",
        {
          rotate: [0, -3, 3, 0],
          scale: [1, 1.02, 1],
        },
        {
          duration: 0.4,
          ease: "easeInOut",
        },
      );
      await animate(
        ".card-stripe",
        {
          opacity: [0, 1, 0],
          x: [-18, 18],
        },
        {
          duration: 0.5,
          ease: "easeInOut",
        },
      );
      animate(
        ".card-chip",
        {
          scale: [1, 1.6, 1],
          opacity: [0.6, 1, 0.6],
        },
        {
          duration: 0.25,
          ease: "easeOut",
        },
      );
    };

    const stop = () => {
      animate(".card-body", { rotate: 0, scale: 1 }, { duration: 0.2 });
      animate(".card-stripe", { opacity: 0, x: 0 }, { duration: 0.2 });
      animate(".card-chip", { scale: 1, opacity: 0.6 }, { duration: 0.2 });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => {
      return {
        startAnimation: start,
        stopAnimation: stop,
      };
    });

    const handleHoverStart = () => {
      start();
    };

    const handleHoverEnd = () => {
      stop();
    };

    return (
      <motion.div
        ref={scope}
        onHoverStart={loop ? undefined : handleHoverStart}
        onHoverEnd={loop ? undefined : handleHoverEnd}
        className={`inline-flex cursor-pointer ${className}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            className="card-body"
            style={{ transformOrigin: "50% 50%" }}
            d="M3 5m0 3a3 3 0 0 1 3 -3h12a3 3 0 0 1 3 3v8a3 3 0 0 1 -3 3h-12a3 3 0 0 1 -3 -3z"
          />

          <motion.path className="card-stripe" d="M3 10l18 0" opacity="0" />

          <motion.path
            className="card-chip"
            style={{ transformOrigin: "7px 15px" }}
            d="M7 15l.01 0"
          />

          <motion.path className="card-number" d="M11 15l2 0" />
        </svg>
      </motion.div>
    );
  },
);

CreditCardIcon.displayName = "CreditCard";

export const CurrencyDollarIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = async () => {
      await animate(
        ".usd-main, .usd-line",
        { pathLength: 0, opacity: 0 },
        { duration: 0 },
      );

      await animate(
        ".usd-line",
        { pathLength: 1, opacity: 1 },
        { duration: 0.25, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );

      await animate(
        ".usd-main",
        { pathLength: 1, opacity: 1 },
        { duration: 0.4, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );

      animate(
        ".usd-symbol",
        { scale: [0.96, 1] },
        { duration: 0.2, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    };

    const stop = () => {
      animate(
        ".usd-main, .usd-line",
        { pathLength: 1, opacity: 1 },
        { duration: 0.2 },
      );
      animate(".usd-symbol", { scale: 1 }, { duration: 0.2 });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => {
      return {
        startAnimation: start,
        stopAnimation: stop,
      };
    });

    const handleHoverStart = () => {
      start();
    };

    const handleHoverEnd = () => {
      stop();
    };

    return (
      <motion.div
        ref={scope}
        onHoverStart={loop ? undefined : handleHoverStart}
        onHoverEnd={loop ? undefined : handleHoverEnd}
        className={`inline-flex cursor-pointer items-center justify-center ${className}`}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path stroke="none" d="M0 0h24v24H0z" fill="none" />
          <motion.g
            className="usd-symbol"
            style={{ transformOrigin: "50% 50%" }}
          >
            <motion.path
              className="usd-main"
              d="M16.7 8a3 3 0 0 0 -2.7 -2h-4a3 3 0 0 0 0 6h4a3 3 0 0 1 0 6h-4a3 3 0 0 1 -2.7 -2"
              pathLength={1}
            />
            <motion.path
              className="usd-line"
              d="M12 3v3m0 12v3"
              pathLength={1}
            />
          </motion.g>
        </svg>
      </motion.div>
    );
  },
);

CurrencyDollarIcon.displayName = "CurrencyDollarIcon";

type CustomAnimation = {
  rotateFrom: number;
  rotateTo: number;
  duration: number;
  ease: IconEasing;
  personDuration: number;
  personEase: IconEasing;
  exitDuration: number;
};

export const AccessibilityIcon = forwardRef<
  AnimatedIconHandle,
  AnimatedIconProps & CustomAnimation
>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", rotateFrom = 0, rotateTo = 360, duration = 1, ease = "easeInOut", personDuration = 0.6, personEase = "easeInOut", exitDuration = 0.3, loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const animationControls = useRef<Array<ReturnType<typeof animate>>>([]);

    const start = async () => {
      animationControls.current.forEach((control: any) => control.stop());
      animationControls.current = [];

      animationControls.current.push(
        animate(
          ".wheel",
          { rotate: [rotateFrom, rotateTo] },
          { duration: duration, ease: ease, repeat: Infinity },
        ),
      );
      animate(
        ".person",
        { y: [0, -2, 0] },
        { duration: personDuration, ease: personEase },
      );
    };

    const stop = () => {
      animationControls.current.forEach((control: any) => control.stop());
      animationControls.current = [];

      animate(".wheel", { rotate: 0 }, { duration: exitDuration });
      animate(".person", { y: 0 }, { duration: exitDuration });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => {
      return {
        startAnimation: start,
        stopAnimation: stop,
      };
    });

    const handleHoverStart = () => {
      start();
    };

    const handleHoverEnd = () => {
      stop();
    };

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : handleHoverStart}
        onHoverEnd={loop ? undefined : handleHoverEnd}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className} `}
      >
        <motion.circle className="person" cx="16" cy="4" r="1" />
        <motion.path className="person" d="m18 19 1-7-6 1" />
        <motion.path className="person" d="m5 8 3-3 5.5 3-2.36 3.5" />
        <motion.g className="wheel" style={{ transformOrigin: "8.5px 17.5px" }}>
          <path d="M4.24 14.5a5 5 0 0 0 6.88 6" />
          <path d="M13.76 17.5a5 5 0 0 0-6.88-6" />
        </motion.g>
      </motion.svg>
    );
  },
);

AccessibilityIcon.displayName = "AccessibilityIcon";

export const BrandBagsFmIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = async () => {
      await animate(
        ".bag",
        {
          rotate: [0, -8, 8, -6, 6, -3, 3, 0],
          y: [0, -5, 0, -3, 0, -2, 0],
        },
        { duration: 0.6, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    };

    const stop = () => {
      animate(".bag", { rotate: 0, y: 0 }, { duration: 0.2 });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => {
      return {
        startAnimation: start,
        stopAnimation: stop,
      };
    });

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 1024 1024"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
      >
        <motion.g className="bag" style={{ originX: "50%", originY: "100%" }}>
          <motion.path
            d="M 561.64 1018.00 C541.03,1019.15 482.25,1019.09 461.00,1017.88 C428.17,1016.02 384.57,1010.43 359.50,1004.88 C276.24,986.43 221.60,959.81 176.15,915.53 C130.14,870.72 104.87,814.62 98.92,744.08 C97.58,728.12 98.21,685.69 99.98,673.00 C100.56,668.88 101.74,660.33 102.61,654.00 C112.42,582.33 148.13,492.03 190.38,432.09 C218.55,392.11 255.44,354.21 296.61,322.93 C307.25,314.85 311.79,313.00 321.00,313.00 C329.04,313.00 333.32,314.38 342.31,319.88 C359.36,330.32 385.72,341.87 407.76,348.57 C432.77,356.17 453.66,360.10 482.00,362.55 C553.43,368.72 624.89,353.61 682.14,320.22 C693.80,313.42 696.44,312.53 705.00,312.57 C714.85,312.61 718.38,314.30 734.00,326.45 C797.73,376.03 845.64,435.94 877.67,506.14 C903.33,562.37 919.23,617.23 925.67,671.74 C928.13,692.55 928.10,740.24 925.61,758.50 C921.88,785.96 914.99,811.78 905.35,834.43 C891.92,866.01 874.02,892.03 848.86,916.54 C835.25,929.80 824.91,938.14 809.00,948.70 C748.01,989.18 666.12,1012.12 561.64,1018.00 ZM 536.19 826.97 C540.81,825.68 545.00,820.86 545.00,816.82 C545.00,812.50 547.56,807.69 550.92,805.71 C553.43,804.22 557.01,804.00 578.26,804.00 C615.47,804.00 633.30,802.14 651.41,796.38 C673.82,789.25 686.65,776.74 691.55,757.22 C692.87,752.00 693.07,745.89 692.76,721.22 C692.35,689.59 691.82,685.50 686.70,675.01 C681.23,663.80 665.70,653.24 646.15,647.43 C641.39,646.02 631.88,643.14 625.00,641.03 C606.42,635.34 582.75,628.19 572.00,625.02 C547.05,617.65 508.45,605.77 504.70,604.29 C498.82,601.98 493.27,598.38 492.04,596.08 C491.47,595.01 491.00,592.36 491.00,590.20 C491.00,582.30 497.66,577.84 509.50,577.82 C522.74,577.81 528.73,582.58 530.47,594.50 C530.87,597.25 531.71,599.83 532.35,600.23 C532.98,600.64 563.97,600.98 601.21,600.98 C666.27,601.00 669.13,600.92 674.21,599.02 C681.74,596.20 686.42,591.92 690.06,584.53 C693.16,578.23 693.20,577.94 692.85,565.76 C692.51,553.79 692.38,553.15 688.72,545.75 C683.93,536.06 676.81,529.50 664.50,523.44 C645.31,513.99 626.50,511.02 585.20,510.94 C565.67,510.91 555.41,510.51 553.09,509.69 C548.74,508.17 546.24,504.73 545.14,498.78 C544.01,492.65 541.66,489.17 537.45,487.42 C532.52,485.36 487.43,485.38 482.49,487.44 C478.47,489.12 475.01,494.22 475.00,498.50 C474.99,502.15 471.94,507.40 468.80,509.17 C467.11,510.13 457.40,510.64 432.00,511.12 C406.48,511.60 395.22,512.20 388.74,513.42 C358.34,519.14 340.06,529.78 331.91,546.50 L 328.50 553.50 L 328.19 578.78 C327.93,599.97 328.14,604.97 329.53,609.74 C335.16,629.13 346.66,638.38 379.50,649.96 C384.45,651.71 390.75,653.74 393.50,654.47 C396.25,655.20 408.85,658.81 421.50,662.49 C449.39,670.59 469.49,676.41 494.00,683.46 C524.12,692.13 528.85,695.08 531.01,706.53 C531.93,711.45 531.93,714.46 531.00,719.97 C529.70,727.74 527.09,731.84 521.63,734.73 C517.07,737.14 505.64,737.00 500.56,734.46 C494.61,731.49 493.04,728.63 492.03,718.81 C490.65,705.44 486.14,698.82 476.50,696.04 C473.62,695.21 455.29,694.97 411.00,695.19 L 349.50 695.50 L 344.64 697.89 C338.22,701.06 333.29,706.13 330.30,712.66 C327.92,717.87 327.87,718.49 328.18,739.30 L 328.50 760.61 L 332.27 768.24 C342.28,788.48 366.04,799.76 405.50,802.99 C412.10,803.53 428.38,803.98 441.68,803.98 C454.99,803.99 467.02,804.44 468.43,804.98 C471.68,806.21 474.99,811.57 475.00,815.60 C475.00,819.92 478.87,825.26 483.03,826.69 C487.42,828.20 530.93,828.43 536.19,826.97 ZM 538.50 289.97 C496.13,293.54 448.71,287.35 418.42,274.29 C400.92,266.74 395.55,263.15 374.23,244.71 C335.15,210.90 308.78,179.14 298.10,153.00 C294.86,145.06 294.63,143.75 294.57,133.00 C294.51,122.90 294.78,120.98 296.76,117.28 C300.36,110.53 304.53,106.44 318.44,96.01 C343.30,77.37 350.32,70.84 389.50,29.94 C408.14,10.48 415.56,6.96 439.53,6.23 C452.01,5.85 456.43,6.13 465.56,7.87 C476.52,9.97 479.33,10.84 509.50,21.49 C533.06,29.80 540.92,31.28 562.00,31.37 C573.44,31.41 582.10,30.94 587.00,29.99 C597.54,27.96 620.67,28.11 628.72,30.27 C635.91,32.20 646.41,37.33 653.28,42.26 C655.91,44.15 665.14,52.63 673.79,61.10 C693.45,80.36 701.04,86.89 714.89,96.47 C726.70,104.63 730.32,108.22 733.07,114.50 C744.54,140.60 718.47,183.79 657.88,239.05 C629.95,264.53 617.87,271.89 589.75,280.53 C576.65,284.56 554.66,288.61 538.50,289.97 Z"
            fill={color}
          />
        </motion.g>
      </motion.svg>
    );
  },
);

BrandBagsFmIcon.displayName = "BrandBagsFmIcon";

export const BananaIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = async () => {
      animate(
        ".peel",
        { pathLength: [0, 1], opacity: [0, 1] },
        { duration: 0.5, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
      await animate(
        ".banana",
        { rotate: [0, -5, 5, -5, 0], y: [0, -2, 0] },
        { duration: 0.6, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    };

    const stop = () => {
      animate(".peel", { pathLength: 1, opacity: 1 }, { duration: 0.2 });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => {
      return {
        startAnimation: start,
        stopAnimation: stop,
      };
    });

    const handleHoverStart = () => {
      start();
    };

    const handleHoverEnd = () => {
      stop();
    };

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : handleHoverStart}
        onHoverEnd={loop ? undefined : handleHoverEnd}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
      >
        <motion.path
          className="peel"
          d="M4 13c3.5-2 8-2 10 2a5.5 5.5 0 0 1 8 5"
          initial={{ pathLength: 1, opacity: 1 }}
        />
        <motion.path
          className="banana"
          style={{ transformOrigin: "center" }}
          d="M5.15 17.89c5.52-1.52 8.65-6.89 7-12C11.55 4 11.5 2 13 2c3.22 0 5 5.5 5 8 0 6.5-4.2 12-10.49 12C5.11 22 2 22 2 20c0-1.5 1.14-1.55 3.15-2.11Z"
        />
      </motion.svg>
    );
  },
);

BananaIcon.displayName = "BananaIcon";

export const CameraOffIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = async () => {
      animate(
        ".slash",
        { scale: 1.1, rotate: 5 },
        { duration: 0.3, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
      animate(".camera", { opacity: 0.4 }, { duration: 0.3, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const });
    };

    const stop = () => {
      animate(
        ".slash",
        { scale: 1, rotate: 0 },
        { duration: 0.2, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
      animate(".camera", { opacity: 1 }, { duration: 0.2, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => {
      return {
        startAnimation: start,
        stopAnimation: stop,
      };
    });

    const handleHoverStart = () => {
      start();
    };

    const handleHoverEnd = () => {
      stop();
    };

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : handleHoverStart}
        onHoverEnd={loop ? undefined : handleHoverEnd}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
      >
        <motion.g className="camera">
          <path d="M14.564 14.558a3 3 0 1 1-4.122-4.121" />
          <path d="M20 20H4a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h1.997a2 2 0 0 0 .819-.175" />
          <path d="M9.695 4.024A2 2 0 0 1 10.004 4h3.993a2 2 0 0 1 1.76 1.05l.486.9A2 2 0 0 0 18.003 7H20a2 2 0 0 1 2 2v7.344" />
        </motion.g>
        <motion.path
          className="slash"
          style={{ transformOrigin: "12px 12px" }}
          d="m2 2 20 20"
        />
      </motion.svg>
    );
  },
);

CameraOffIcon.displayName = "CameraOffIcon";

export const InfoCircleIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = useCallback(async () => {
      await animate(
        ".info-circle-i",
        {
          pathLength: [0, 1],
        },
        {
          duration: 0.3,
          ease: "easeOut",
        },
      );

      animate(
        ".info-line-i",
        {
          pathLength: [0, 1],
        },
        {
          duration: 0.4,
          ease: "easeOut",
        },
      );
    }, [animate]);

    const stop = useCallback(() => {
      animate(
        ".info-circle-i, .info-line-i",
        { pathLength: 1 },
        { duration: 0.2, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    }, [animate]);

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
      >
        <motion.path stroke="none" d="M0 0h24v24H0z" fill="none" />
        <motion.path
          d="M3 12a9 9 0 1 0 18 0a9 9 0 0 0 -18 0"
          className="info-circle"
        />
        <motion.path d="M12 9h.01" className="info-circle-i" />
        <motion.path d="M11 12h1v4h1" className="info-line-i" />
      </motion.svg>
    );
  },
);

InfoCircleIcon.displayName = "InfoCircleIcon";

export const UsersGroupIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = useCallback(async () => {
      // Center user bounces
      animate(
        ".user-center",
        {
          y: -2,
          scale: 1.05,
        },
        {
          duration: 0.25,
          ease: "easeOut",
        },
      );

      // Left user moves slightly left
      animate(
        ".user-left",
        {
          x: -1,
          scale: 1.02,
        },
        {
          duration: 0.3,
          ease: "easeOut",
          delay: 0.05,
        },
      );

      // Right user moves slightly right
      animate(
        ".user-right",
        {
          x: 1,
          scale: 1.02,
        },
        {
          duration: 0.3,
          ease: "easeOut",
          delay: 0.05,
        },
      );
    }, [animate]);

    const stop = useCallback(async () => {
      animate(
        ".user-center",
        {
          x: 0,
          y: 0,
          scale: 1,
        },
        {
          duration: 0.25,
          ease: "easeInOut",
        },
      );

      animate(
        ".user-left",
        {
          x: 0,
          y: 0,
          scale: 1,
        },
        {
          duration: 0.25,
          ease: "easeInOut",
        },
      );

      animate(
        ".user-right",
        {
          x: 0,
          y: 0,
          scale: 1,
        },
        {
          duration: 0.25,
          ease: "easeInOut",
        },
      );
    }, [animate]);

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />

        {/* Center user */}
        <motion.g className="user-center">
          <path d="M10 13a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
          <path d="M8 21v-1a2 2 0 0 1 2 -2h4a2 2 0 0 1 2 2v1" />
        </motion.g>

        {/* Right user */}
        <motion.g className="user-right">
          <path d="M15 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
          <path d="M17 10h2a2 2 0 0 1 2 2v1" />
        </motion.g>

        {/* Left user */}
        <motion.g className="user-left">
          <path d="M5 5a2 2 0 1 0 4 0a2 2 0 0 0 -4 0" />
          <path d="M3 13v-1a2 2 0 0 1 2 -2h2" />
        </motion.g>
      </motion.svg>
    );
  },
);

UsersGroupIcon.displayName = "UsersGroupIcon";

export const UserPlusIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = useCallback(async () => {
      // User bounces slightly
      animate(
        ".user-avatar",
        {
          scale: 1.05,
          y: -1,
        },
        {
          duration: 0.25,
          ease: "easeOut",
        },
      );

      // Plus sign expands and rotates
      animate(
        ".plus-sign",
        {
          scale: 1.15,
          rotate: 90,
        },
        {
          duration: 0.3,
          ease: "easeOut",
        },
      );
    }, [animate]);

    const stop = useCallback(async () => {
      animate(
        ".user-avatar",
        {
          scale: 1,
          y: 0,
        },
        {
          duration: 0.2,
          ease: "easeInOut",
        },
      );

      animate(
        ".plus-sign",
        {
          scale: 1,
          rotate: 0,
        },
        {
          duration: 0.25,
          ease: "easeInOut",
        },
      );
    }, [animate]);

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />

        {/* User avatar */}
        <motion.g
          className="user-avatar"
          style={{ transformOrigin: "50% 50%" }}
        >
          <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
          <path d="M6 21v-2a4 4 0 0 1 4 -4h4" />
        </motion.g>

        {/* Plus sign */}
        <motion.g
          className="plus-sign"
          style={{ transformOrigin: "19px 19px" }}
        >
          <path d="M16 19h6" />
          <path d="M19 16v6" />
        </motion.g>
      </motion.svg>
    );
  },
);

UserPlusIcon.displayName = "UserPlusIcon";

export const UserCheckIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = useCallback(async () => {
      // User bounces slightly
      animate(
        ".user-avatar",
        {
          scale: 1.05,
          y: -1,
        },
        {
          duration: 0.25,
          ease: "easeOut",
        },
      );

      // Checkmark draws in and scales
      animate(
        ".check-mark",
        {
          pathLength: [0, 1],
          scale: 1.1,
        },
        {
          duration: 0.4,
          ease: "easeOut",
        },
      );
    }, [animate]);

    const stop = useCallback(async () => {
      animate(
        ".user-avatar",
        {
          scale: 1,
          y: 0,
        },
        {
          duration: 0.2,
          ease: "easeInOut",
        },
      );

      animate(
        ".check-mark",
        {
          pathLength: 1,
          scale: 1,
        },
        {
          duration: 0.25,
          ease: "easeInOut",
        },
      );
    }, [animate]);

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
      >
        <path stroke="none" d="M0 0h24v24H0z" fill="none" />

        {/* User avatar */}
        <motion.g
          className="user-avatar"
          style={{ transformOrigin: "50% 50%" }}
        >
          <path d="M8 7a4 4 0 1 0 8 0a4 4 0 0 0 -8 0" />
          <path d="M6 21v-2a4 4 0 0 1 4 -4h4" />
        </motion.g>

        {/* Checkmark */}
        <motion.path
          d="M15 19l2 2l4 -4"
          className="check-mark"
          style={{ transformOrigin: "18px 19px" }}
          initial={{ pathLength: 1 }}
        />
      </motion.svg>
    );
  },
);

UserCheckIcon.displayName = "UserCheckIcon";

export const MapPinIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();
    const isAnimatingRef = useRef(false);

    const start = useCallback(async () => {
      if (isAnimatingRef.current) return;
      isAnimatingRef.current = true;

      while (isAnimatingRef.current) {
        // High-Intensity Beacon Pulse (Opacity ONLY, NO Scale, NO Motion)
        await animate(
          ".pin-dot",
          { opacity: [1, 0.4, 1] },
          { duration: 0.6, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
        );
        if (!isAnimatingRef.current) break;
      }
    }, [animate]);

    const stop = useCallback(() => {
      isAnimatingRef.current = false;
      animate(".pin-dot", { opacity: 1 }, { duration: 0.3 });
    }, [animate]);

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        style={{ overflow: "visible" }}
      >
        {/* Pin Body - Perfectly Static */}
        <path d="M20 10c0 4.993-5.539 10.193-7.399 11.799a1 1 0 0 1-1.202 0C9.539 20.193 4 14.993 4 10a8 8 0 0 1 16 0" />

        {/* Inner Dot - The Beacon */}
        <motion.circle className="pin-dot" cx="12" cy="10" r="3" />
      </motion.svg>
    );
  },
);

MapPinIcon.displayName = "MapPinIcon";

export const LogoutIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = useCallback(async () => {
      await animate(
        ".logout-arrow, .logout-arrow-bottom",
        {
          x: [0, 6, 0],
        },
        {
          duration: 0.3,
          ease: "easeInOut",
        },
      );
      animate(
        ".logout-door",
        {
          x: [0, -2, 0],
        },
        {
          duration: 0.25,
          ease: "easeOut",
        },
      );
    }, [animate]);

    const stop = useCallback(() => {
      animate(
        ".logout-arrow, .logout-arrow-bottom, .logout-door",
        { x: 0 },
        { duration: 0.2, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    }, [animate]);

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.div
        ref={scope}
        className={`inline-flex cursor-pointer ${className}`}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={size}
          height={size}
          viewBox="0 0 24 24"
          fill="none"
          stroke={color}
          strokeWidth={strokeWidth}
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <motion.path
            className="logout-door"
            style={{ transformOrigin: "50% 50%" }}
            d="M14 8v-2a2 2 0 0 0 -2 -2h-7a2 2 0 0 0 -2 2v12a2 2 0 0 0 2 2h7a2 2 0 0 0 2 -2v-2"
          />

          <motion.path className="logout-arrow" d="M9 12h12" />

          <motion.path className="logout-arrow-bottom" d="M18 15l3 -3l-3 -3" />
        </svg>
      </motion.div>
    );
  },
);

LogoutIcon.displayName = "LogoutIcon";

export const LetterPIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = async () => {
      // Head bob animation
      animate(
        ".p-shape",
        { x: [0, 2, -1, 1, 0] },
        { duration: 0.4, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    };

    const stop = () => {
      animate(".p-shape", { x: 0 }, { duration: 0.2 });
    };

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        style={{ overflow: "visible" }}
      >
        <motion.path className="p-shape" d="M7 20v-16h5.5a4 4 0 0 1 0 9h-5.5" />
      </motion.svg>
    );
  },
);

LetterPIcon.displayName = "LetterPIcon";

export const ShieldCheckIcon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = useCallback(async () => {
      animate(
        ".shield-body",
        { scale: [1, 1.05, 1] },
        { duration: 0.35, ease: "easeOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );

      await animate(
        ".shield-check",
        { pathLength: [0, 1], opacity: [0, 1] },
        { duration: 0.3, ease: "easeInOut" , repeat: loop ? Infinity : 0, repeatType: "reverse" as const },
      );
    }, [animate]);

    const stop = useCallback(() => {
      animate(".shield-body", { scale: 1 }, { duration: 0.2 });
      animate(
        ".shield-check",
        { pathLength: 1, opacity: 1 },
        { duration: 0.2 },
      );
    }, [animate]);

    useLoopingAnimation(loop, start, stop);
    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        style={{ overflow: "visible" }}
      >
        <motion.path
          className="shield-body"
          style={{ transformOrigin: "50% 50%" }}
          d="M11.46 20.846a12 12 0 0 1 -7.96 -14.846a12 12 0 0 0 8.5 -3a12 12 0 0 0 8.5 3a12 12 0 0 1 -.09 7.06"
        />

        <motion.path
          className="shield-check"
          d="M15 19l2 2l4 -4"
          initial={{ pathLength: 1, opacity: 1 }}
        />
      </motion.svg>
    );
  },
);

ShieldCheckIcon.displayName = "ShieldCheck";

export const Cloud1Icon = forwardRef<AnimatedIconHandle, AnimatedIconProps>(
  ({ size = 24, color = "currentColor", strokeWidth = 2, className = "", loop = false }, ref) => {
    const [scope, animate] = useAnimate();

    const start = useCallback(() => {
      animate(
        scope.current,
        { scale: [1, 1.05, 1] },
        {
          duration: 2,
          repeat: Infinity,
          ease: "easeInOut",
        },
      );
    }, [animate, scope]);

    const stop = useCallback(() => {
      animate(scope.current, { scale: 1 }, { duration: 0.3 });
    }, [animate, scope]);

    useImperativeHandle(ref, () => ({
      startAnimation: start,
      stopAnimation: stop,
    }));

    useLoopingAnimation(loop, start, stop);

    return (
      <motion.svg
        ref={scope}
        onHoverStart={loop ? undefined : start}
        onHoverEnd={loop ? undefined : stop}
        xmlns="http://www.w3.org/2000/svg"
        width={size}
        height={size}
        viewBox="0 0 24 24"
        fill="none"
        stroke={color}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        className={`cursor-pointer ${className}`}
        style={{ overflow: "visible" }}
      >
        <path d="M17.5 19H9a7 7 0 1 1 6.71-9h1.79a4.5 4.5 0 1 1 0 9Z" />
      </motion.svg>
    );
  }
);

Cloud1Icon.displayName = "Cloud1Icon";

